import React, { type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import assistantI18n from './i18n';

import type { AIShellFeatures } from './AIShellWrapper';
import type { APIService, IContextDetection, UserService } from './interfaces/services';
import type { PageContext } from './services/ContextDetectionService';
import type {
  AgentChoiceOption,
  AgentContext,
  AgentMessage,
  AgentNames,
  AppActionContext,
  AppActionConfig,
  AsyncMessageStatusResponse,
  GetConversationsResponse,
  Message,
  MessageButtonSpec,
  MessageContent,
  PostMessageResponse,
  SourceRef,
} from './types';
import { useConversationApi } from './useConversationApi';
import { pollUntilDone } from './utils/polling';

if (typeof crypto.randomUUID !== 'function') {
  crypto.randomUUID = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] % 16) + 0x40;
    bytes[8] = (bytes[8] % 64) + 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
}

export type AppActionHandler = (actionData: Record<string, any>, context: AppActionContext) => Promise<void>;

type AIShellContextType = {
  isOpen: boolean;
  toggleShell: () => void;
  openShell: () => void;
  isExpanded: boolean;
  toggleExpanded: () => void;
  chatView: ChatViewEnum;
  tabIndex: TabsEnum;
  setTabIndex: (ti: TabsEnum) => void;
  agentThinking: boolean;
  selectedAgent: AgentNames | string | null;
  setSelectedAgent: (name: AgentNames | string | null) => void;
  getConversations: (limit?: number, offset?: number, search?: string) => Promise<GetConversationsResponse>;
  messageSendError: string | null;
  openAgentShell: (agent: AgentNames, context?: AgentContext) => void;
  openIntroFor: (agent: AgentNames | string) => void;
  backToAgentBrowser: () => void;
  currentConversation: CurrentConversation | null;
  openNewChat: () => void;
  send: (message: string, options?: { context?: AgentContext }) => Promise<void>;
  openChatAndSend: (message: string) => Promise<void>;
  openConversation: (id: CurrentConversation['id']) => Promise<void>;
  /** Appends a local agent message bubble (e.g. after an app_action applies UI state). */
  appendAgentChatMessage: (contents: string) => void;
  // Context detection functionality
  availableContext: PageContext;
  updateContext: (context: Partial<PageContext>) => void;
  refreshContext: () => void;
  // App action handler registry
  registerAppActionHandler: (actionName: string, handler: AppActionHandler) => void;
  unregisterAppActionHandler: (actionName: string) => void;
  // Message buttons registry
  registerMessageButtons: (messageId: string, actions: MessageButtonSpec[]) => void;
  unregisterMessageButtons: (messageId: string) => void;
  getMessageButtons: (messageId: string) => MessageButtonSpec[];
  // App action for agent messages (labels + optional inline buttons)
  registerMessageAppActions: (messageId: string, actions: AppActionConfig[]) => void;
  getMessageAppActions: (messageId: string) => AppActionConfig[];
  // Panel-level buttons (rendered at top of chat, above messages)
  registerPanelButtons: (buttons: MessageButtonSpec[]) => void;
  getPanelButtons: () => MessageButtonSpec[];
  // Multiple-choice options from the latest agent response
  choices: AgentChoiceOption[];
  // Feature toggles passed from the host app
  features: AIShellFeatures;
};

export const AIShellContext = React.createContext<AIShellContextType | null>(null);

export const useAIShellContext = () => {
  const context = useContext(AIShellContext);

  if (context === null) {
    throw Error('Trying to use AIShellContext outside of AIShellContextProvider!');
  }

  return context;
};

/**
 * Safe version of useAIShellContext that returns null when outside provider.
 * Use this when the component may or may not be inside an AIShellContextProvider.
 */
export const useAIShellContextSafe = () => {
  return useContext(AIShellContext);
};

const createUserMessage = (contents: string, id?: string): Message => ({
  id: id ?? crypto.randomUUID(),
  type: 'user',
  contents,
});
const createAgentMessage = (
  contents: string,
  id?: string,
  citedSources?: SourceRef[],
  content?: MessageContent[],
): Message => ({
  id: id ?? crypto.randomUUID(),
  type: 'agent',
  contents,
  ...(citedSources?.length ? { cited_sources: citedSources } : {}),
  ...(content?.length ? { content } : {}),
});
const parseAgentMessage = (agentMessage: AgentMessage): [Message, Message] => {
  return [
    createUserMessage(agentMessage.input_message, `${agentMessage.id}:user`),
    createAgentMessage(
      agentMessage.response_message,
      `${agentMessage.id}:agent`,
      agentMessage.cited_sources,
      agentMessage.content,
    ),
  ];
};

enum TabsEnum {
  CHAT,
  HISTORY,
}

export enum ChatViewEnum {
  BROWSER,
  INTRO,
  CHAT,
}

type CurrentConversation = {
  id: string;
  messages: Array<Message>;
};

type AIShellContextProviderProps = PropsWithChildren<{
  userService: UserService;
  apiService: APIService;
  /**
   * Context detection adapter for URL-based context extraction.
   * Required for context-specific agents to be filtered correctly.
   * If not provided, no context detection occurs (only global agents shown).
   */
  contextDetection?: IContextDetection;
  features?: AIShellFeatures;
}>;

const useDefaultContext = (_userService: UserService): AgentContext => {
  return useMemo(
    () => ({}), // Empty context - backend gets user from request
    [],
  );
};

const DEFAULT_FEATURES: AIShellFeatures = {};

export const AIShellContextProvider = ({
  children,
  userService,
  apiService,
  contextDetection,
  features = DEFAULT_FEATURES,
}: AIShellContextProviderProps) => {
  const { t } = useTranslation('assistant');
  const [isOpen, setIsOpen] = useState<AIShellContextType['isOpen']>(false);
  const [isExpanded, setIsExpanded] = useState<AIShellContextType['isExpanded']>(false);
  const toggleShell: AIShellContextType['toggleShell'] = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      // Reset expansion when closing so re-opening returns to the default collapsed view.
      if (!next) {
        setIsExpanded(false);
      }
      return next;
    });
  }, []);
  const openShell: AIShellContextType['openShell'] = useCallback(() => {
    setIsOpen(true);
  }, []);
  const toggleExpanded: AIShellContextType['toggleExpanded'] = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const {
    createConversation,
    sendMessage,
    sendMessageAsync,
    getMessageStatus,
    getConversations,
    getSingleConversation,
    abortController,
  } = useConversationApi(apiService);

  const asyncPollControllerRef = useRef<AbortController | null>(null);

  const [tabIndex, setTabIndex] = useState<TabsEnum>(TabsEnum.CHAT);
  const [chatView, setChatView] = useState<ChatViewEnum>(ChatViewEnum.BROWSER);

  const [selectedAgent, setSelectedAgentState] = useState<AIShellContextType['selectedAgent']>(null);
  const selectedAgentRef = useRef<AIShellContextType['selectedAgent']>(null);
  const setSelectedAgent = useCallback((agent: AIShellContextType['selectedAgent']) => {
    selectedAgentRef.current = agent;
    setSelectedAgentState(agent);
  }, []);
  const [backgroundContext, setBackgroundContext] = useState<AgentContext>({});
  const [currentConversation, setCurrentConversation] = useState<AIShellContextType['currentConversation']>(null);
  const [messageSendError, setMessageSendError] = useState<string | null>(null);
  const defaultContext = useDefaultContext(userService);
  const [agentThinking, setAgentThinking] = useState<AIShellContextType['agentThinking']>(false);
  const [choices, setChoices] = useState<AgentChoiceOption[]>([]);

  // Context detection state - empty if no adapter provided
  const [availableContext, setAvailableContext] = useState<PageContext>(() => contextDetection?.detectContext() ?? {});

  // App action handler registry
  const appActionHandlersRef = useRef<Map<string, AppActionHandler>>(new Map());

  const registerAppActionHandler = useCallback((name: string, handler: AppActionHandler) => {
    appActionHandlersRef.current.set(name, handler);
  }, []);

  const unregisterAppActionHandler = useCallback((name: string) => {
    appActionHandlersRef.current.delete(name);
  }, []);

  // Message buttons registry
  const [messageButtonsMap, setMessageButtonsMap] = useState<Map<string, MessageButtonSpec[]>>(new Map());

  const registerMessageButtons = useCallback((messageId: string, actions: MessageButtonSpec[]) => {
    setMessageButtonsMap((prev) => {
      const next = new Map(prev);
      next.set(messageId, actions);
      return next;
    });
  }, []);

  const unregisterMessageButtons = useCallback((messageId: string) => {
    setMessageButtonsMap((prev) => {
      const next = new Map(prev);
      next.delete(messageId);
      return next;
    });
  }, []);

  const getMessageButtons = useCallback(
    (messageId: string): MessageButtonSpec[] => {
      return messageButtonsMap.get(messageId) ?? [];
    },
    [messageButtonsMap],
  );

  // App action registry (labels + optional inline buttons per agent message)
  const [messageAppActionsMap, setMessageAppActionsMap] = useState<Map<string, AppActionConfig[]>>(new Map());

  const registerMessageAppActions = useCallback((messageId: string, actions: AppActionConfig[]) => {
    setMessageAppActionsMap((prev) => {
      const next = new Map(prev);
      next.set(messageId, actions);
      return next;
    });
  }, []);

  const getMessageAppActions = useCallback(
    (messageId: string): AppActionConfig[] => {
      return messageAppActionsMap.get(messageId) ?? [];
    },
    [messageAppActionsMap],
  );

  // Panel-level buttons (rendered at top of chat)
  const [panelButtons, setPanelButtons] = useState<MessageButtonSpec[]>([]);

  const registerPanelButtons = useCallback((buttons: MessageButtonSpec[]) => {
    setPanelButtons(buttons);
  }, []);

  const getPanelButtons = useCallback((): MessageButtonSpec[] => {
    return panelButtons;
  }, [panelButtons]);

  // Context detection methods
  const updateContext = useCallback((context: Partial<PageContext>) => {
    setAvailableContext((prev) => ({ ...prev, ...context }));
  }, []);

  const refreshContext = useCallback(() => {
    if (contextDetection) {
      setAvailableContext(contextDetection.detectContext());
    }
  }, [contextDetection]);

  // Subscribe to context changes via adapter
  useEffect(() => {
    if (!contextDetection) {
      return undefined;
    }
    return contextDetection.subscribeToContextChanges((newContext) => {
      setAvailableContext(newContext);
    });
  }, [contextDetection]);

  const extractAndSetMessageSendError = useCallback(
    (error: unknown) => {
      if (error instanceof Error && error.message) {
        // Try to extract nested error message from strings like:
        // "AssistKit routing failed. Error code: 400 - {'error': {'message': '...'}}"
        const nestedMatch = error.message.match(/'message':\s*'([^']+)'/);
        setMessageSendError(nestedMatch ? nestedMatch[1] : error.message);
        return;
      }
      setMessageSendError(t('ai_shell.messages.error_sending_message'));
    },
    [t],
  );

  const openAgentShell: AIShellContextType['openAgentShell'] = useCallback(
    (agent, additionalContext) => {
      setSelectedAgent(agent);
      setMessageSendError(null);
      setCurrentConversation(null);
      setMessageButtonsMap(new Map());
      setMessageAppActionsMap(new Map());
      setPanelButtons([]);
      setChoices([]);
      abortController?.abort();
      setBackgroundContext((prevContext) => ({ ...prevContext, ...(additionalContext ?? {}) }));
      setIsOpen(true);
      setTabIndex(TabsEnum.CHAT);
      setChatView(ChatViewEnum.INTRO);
    },
    [abortController, setSelectedAgent],
  );

  const openIntroFor: AIShellContextType['openIntroFor'] = useCallback(
    (agent) => {
      setSelectedAgent(agent);
      setMessageSendError(null);
      setCurrentConversation(null);
      setMessageButtonsMap(new Map());
      setMessageAppActionsMap(new Map());
      setPanelButtons([]);
      setChoices([]);
      abortController?.abort();

      // Use the current available context (already managed by adapter subscription)
      // Convert PageContext to AgentContext (only string/number values)
      const agentContext: AgentContext = {};
      Object.entries(availableContext).forEach(([key, value]) => {
        if (value && (typeof value === 'string' || typeof value === 'number')) {
          agentContext[key] = value;
        }
      });

      setBackgroundContext(agentContext);
      setIsOpen(true);
      setTabIndex(TabsEnum.CHAT);
      setChatView(ChatViewEnum.INTRO);
    },
    [abortController, availableContext, setSelectedAgent],
  );

  const backToAgentBrowser: AIShellContextType['backToAgentBrowser'] = useCallback(() => {
    setSelectedAgent(null);
    setIsOpen(true);
    setTabIndex(TabsEnum.CHAT);
    setChatView(ChatViewEnum.BROWSER);
  }, [setSelectedAgent]);

  const openNewChat: AIShellContextType['openNewChat'] = useCallback(() => {
    setSelectedAgent(null);
    abortController?.abort();
    setMessageSendError(null);
    setCurrentConversation(null);
    setMessageButtonsMap(new Map());
    setMessageAppActionsMap(new Map());
    setPanelButtons([]);
    setChoices([]);
    setIsOpen(true);
    setTabIndex(TabsEnum.CHAT);
    setChatView(ChatViewEnum.BROWSER);
  }, [abortController, setSelectedAgent]);

  const send: AIShellContextType['send'] = useCallback(
    async (message, options) => {
      // Convert PageContext to AgentContext (only string/number values)
      const filteredAvailableContext: AgentContext = {};
      Object.entries(availableContext).forEach(([key, value]) => {
        if (value && (typeof value === 'string' || typeof value === 'number')) {
          filteredAvailableContext[key] = value;
        }
      });
      const resolvedContext = {
        ...defaultContext,
        ...backgroundContext,
        ...filteredAvailableContext,
        ...options?.context,
      };
      let conversationId: string | null = null;

      // Clear any previous error and choices when starting a new send
      setMessageSendError(null);
      setChoices([]);

      if (currentConversation === null) {
        const agent = selectedAgentRef.current;
        if (agent === null) {
          throw Error('Trying to start a conversation without agent selected!');
        }
        const name = message.slice(0, 100);
        try {
          const conversation = await createConversation({
            name,
            agent_name: agent,
            context_metadata: resolvedContext,
          });
          conversationId = conversation.id;
        } catch (err) {
          extractAndSetMessageSendError(err);
          return;
        }
      } else {
        conversationId = currentConversation.id;
      }

      if (conversationId === null) {
        throw Error('Current conversation id is null! This should be impossible...');
      }
      // this reassign is to convince TS that the id is not null at this point
      const currentConversationId: string = conversationId;

      setAgentThinking(true);
      try {
        const userMessage = createUserMessage(message);
        setCurrentConversation((prevConversation: CurrentConversation | null) => {
          if (prevConversation === null) {
            return {
              id: currentConversationId,
              messages: [userMessage],
            };
          }
          return {
            ...prevConversation,
            messages: [...prevConversation.messages, userMessage],
          };
        });

        const payload = { input_message: message, context: resolvedContext };
        let messageResponse: PostMessageResponse;

        try {
          asyncPollControllerRef.current?.abort();
          const pollController = new AbortController();
          asyncPollControllerRef.current = pollController;

          const submitResult = await sendMessageAsync(currentConversationId, payload);

          const completedResult = await pollUntilDone<AsyncMessageStatusResponse>(
            () => getMessageStatus(currentConversationId, submitResult.request_id),
            (r) => r.status !== 'pending',
            pollController.signal,
          );

          if (completedResult.status === 'error') {
            throw new Error(completedResult.error);
          }

          messageResponse = (completedResult as { status: 'completed'; data: PostMessageResponse }).data;
        } catch (asyncErr) {
          if (asyncErr instanceof DOMException && asyncErr.name === 'AbortError') {
            throw asyncErr;
          }
          // Fallback to synchronous send if async endpoint is unavailable (404/405)
          const errStatus =
            asyncErr && typeof asyncErr === 'object'
              ? (asyncErr as any).response?.status ?? (asyncErr as any).status
              : undefined;
          const isEndpointMissing = errStatus === 404 || errStatus === 405;
          if (isEndpointMissing) {
            messageResponse = await sendMessage(currentConversationId, payload);
          } else {
            throw asyncErr;
          }
        }

        const agentMessage = createAgentMessage(
          messageResponse.response_message,
          `${messageResponse.id}:agent`,
          messageResponse.cited_sources,
          messageResponse.content,
        );
        setCurrentConversation((prevConversation) => {
          if (prevConversation === null) {
            throw Error('Conversation does not exist after sending first message! This should be impossible...');
          }
          return {
            ...prevConversation,
            messages: [...prevConversation.messages, agentMessage],
          };
        });

        if (messageResponse.app_actions?.length) {
          const actionContext: AppActionContext = {
            userMessageId: userMessage.id,
            agentMessageId: agentMessage.id,
          };
          const actionConfigs: AppActionConfig[] = messageResponse.app_actions.map((a) => ({ name: a.name }));
          setMessageAppActionsMap((prev) => {
            const next = new Map(prev);
            next.set(agentMessage.id, actionConfigs);
            return next;
          });
          const results = await Promise.allSettled(
            messageResponse.app_actions.map(async (appAction) => {
              const handler = appActionHandlersRef.current.get(appAction.name);
              if (handler) {
                await handler(appAction.action_data, actionContext);
              } else {
                console.error('[AIShell] No handler registered for app_action:', appAction.name);
              }
            }),
          );
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              console.error(
                '[AIShell] app_action handler failed:',
                messageResponse.app_actions?.[index]?.name,
                result.reason,
              );
            }
          });
        }

        if (messageResponse.choices?.length) {
          setChoices(messageResponse.choices);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        extractAndSetMessageSendError(err);
      } finally {
        setAgentThinking(false);
      }
    },
    [
      availableContext,
      defaultContext,
      backgroundContext,
      currentConversation,
      createConversation,
      extractAndSetMessageSendError,
      sendMessageAsync,
      getMessageStatus,
      sendMessage,
    ],
  );

  const openChatAndSend: AIShellContextType['openChatAndSend'] = useCallback(
    async (message) => {
      setIsOpen(true);
      setTabIndex(TabsEnum.CHAT);
      setChatView(ChatViewEnum.CHAT);
      await send(message);
    },
    [send],
  );

  const openConversation: AIShellContextType['openConversation'] = useCallback(
    async (id) => {
      const conversationLoaded = await getSingleConversation(id);
      setMessageSendError(null);
      setMessageButtonsMap(new Map());
      setMessageAppActionsMap(new Map());
      setPanelButtons([]);
      setChoices([]);
      setSelectedAgent(conversationLoaded.agent_name as AgentNames);
      setBackgroundContext(conversationLoaded.context_metadata);
      setCurrentConversation({ id, messages: conversationLoaded.messages.flatMap(parseAgentMessage) });
      setIsOpen(true);
      setTabIndex(TabsEnum.CHAT);
      setChatView(ChatViewEnum.CHAT);
    },
    [getSingleConversation, setSelectedAgent],
  );

  const appendAgentChatMessage: AIShellContextType['appendAgentChatMessage'] = useCallback((contents: string) => {
    const trimmed = contents.trim();
    if (!trimmed) {
      return;
    }
    setCurrentConversation((prev) => {
      if (prev === null) {
        return prev;
      }
      return {
        ...prev,
        messages: [...prev.messages, createAgentMessage(trimmed)],
      };
    });
  }, []);

  const shellContextValue = useMemo(
    () => ({
      isOpen,
      toggleShell,
      openShell,
      isExpanded,
      toggleExpanded,
      chatView,
      tabIndex,
      setTabIndex,
      agentThinking,
      selectedAgent,
      setSelectedAgent,
      getConversations,
      messageSendError,
      openAgentShell,
      openIntroFor,
      backToAgentBrowser,
      currentConversation,
      openNewChat,
      send,
      openChatAndSend,
      openConversation,
      appendAgentChatMessage,
      // Context detection properties
      availableContext,
      updateContext,
      refreshContext,
      // App action handler registry
      registerAppActionHandler,
      unregisterAppActionHandler,
      // Message buttons registry
      registerMessageButtons,
      unregisterMessageButtons,
      getMessageButtons,
      registerMessageAppActions,
      getMessageAppActions,
      registerPanelButtons,
      getPanelButtons,
      choices,
      features,
    }),
    [
      isOpen,
      toggleShell,
      openShell,
      isExpanded,
      toggleExpanded,
      chatView,
      tabIndex,
      setTabIndex,
      agentThinking,
      selectedAgent,
      setSelectedAgent,
      getConversations,
      messageSendError,
      openAgentShell,
      openIntroFor,
      backToAgentBrowser,
      currentConversation,
      openNewChat,
      send,
      openChatAndSend,
      openConversation,
      appendAgentChatMessage,
      // Context detection dependencies
      availableContext,
      updateContext,
      refreshContext,
      // App action handler registry
      registerAppActionHandler,
      unregisterAppActionHandler,
      // Message buttons registry
      registerMessageButtons,
      unregisterMessageButtons,
      getMessageButtons,
      registerMessageAppActions,
      getMessageAppActions,
      registerPanelButtons,
      getPanelButtons,
      choices,
      features,
    ],
  );
  return (
    <I18nextProvider i18n={assistantI18n}>
      <AIShellContext.Provider value={shellContextValue}>{children}</AIShellContext.Provider>
    </I18nextProvider>
  );
};
